import { OnlineOrderingNotConfigured } from '@templates/OnlineOrderingNotConfigured';
import { createMerchantPath } from '@domain/merchants/hooks/useMerchantPath';
import { GetServerSideProps } from 'next';
import { QueryClient } from 'react-query';
import { dehydrate } from 'react-query/hydration';
import nookies from 'nookies';
import { ECookies, EFirestoreCollections } from '@olo-web/types/enums';
import { v4 as uuid } from 'uuid';
import { getLDUserValuesFromCtx } from '@olo-web/utils';
import {
  getFeatureFlags,
  ldCookieOptions,
  scopeFlagsForClientResponse,
} from '@olo-web/services/launchDarkly';
import { getMerchantThemeSSR } from '@domain/merchants/functions/getMerchantThemeSSR';
import {
  configureMerchantData,
  configureMerchantGroupData,
} from '@domain/merchants/functions/configureMerchantData';
import { getMerchant } from '@olo-web/services/emagine/getMerchant';
import { getMerchantGroup } from '@olo-web/services/emagine/getMerchantGroup';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  try {
    const { merchantId, ip } = getLDUserValuesFromCtx(ctx);

    if (!merchantId) return { notFound: true };

    const cookie = nookies.get(ctx);
    const userKey = cookie[ECookies.ldUserKey] || uuid();
    if (!cookie[ECookies.ldUserKey]) nookies.set(ctx, ECookies.ldUserKey, userKey, ldCookieOptions);

    const [featureFlags, merchantRes, merchantGroupRes] = await Promise.all([
      getFeatureFlags(userKey, merchantId as string, ip as string),
      getMerchant(merchantId),
      getMerchantGroup(merchantId as string),
    ]);
    const merchant = configureMerchantData(
      merchantRes?.data,
      featureFlags?.disallowDoorDashDriveOrders
    );
    const merchantGroup = configureMerchantGroupData(
      merchantGroupRes?.data,
      featureFlags?.disallowDoorDashDriveOrders
    );

    // Fetch the merchant and if ordering is unavailable redirect to ordering-unavailable
    if (merchant?.isOrderingAvailable) {
      return {
        redirect: {
          destination: createMerchantPath({
            merchantId: merchantId as string,
            merchant,
            groupId: merchantGroup?.id,
          }),
          permanent: false,
        },
      };
    }

    const queryClient = new QueryClient();

    const promises = [
      queryClient.prefetchQuery(['merchant', merchantId], () => merchant),
      queryClient.prefetchQuery(
        ['merchant-group', merchantId],
        // @ts-ignore
        () => merchantGroup
      ),
      queryClient.prefetchQuery(
        ['feature-flags', merchantId, userKey],
        //@ts-ignore
        () => scopeFlagsForClientResponse(featureFlags, merchantId as string)
      ),
    ];

    if (!featureFlags?.hideMerchantTheming) {
      promises.push(
        queryClient.prefetchQuery(
          [EFirestoreCollections.MERCHANT_THEME, merchantId],
          // @ts-ignore
          () => getMerchantThemeSSR(merchantId)
        )
      );
    }

    await Promise.all(promises);

    return {
      props: {
        dehydratedState: dehydrate(queryClient),
      },
    };
  } catch (e) {
    if (e?.response?.status === 404) {
      return {
        notFound: true,
      };
    }

    throw e;
  }
};

export default OnlineOrderingNotConfigured;
